import { Link } from "react-router-dom";
import { MessageCircle, BookOpen, Heart, HelpCircle } from "lucide-react";
import type { Topic } from "@/types";

interface TopicCardProps {
  topic: Topic;
  className?: string;
}

/**
 * Card component displaying a topic summary with category styling
 *
 * @param topic - Topic data to display
 * @param className - Additional CSS classes
 * @returns Styled topic card with link to chat
 */
export function TopicCard({ topic, className = "" }: TopicCardProps) {
  // Get category-specific styles and icon
  const getCategoryStyle = () => {
    switch (topic.category) {
      case "study":
        return {
          bg: "bg-study-light",
          border: "border-study/20",
          icon: <BookOpen size={18} className="text-study" />,
          label: "Study",
        };
      case "life":
        return {
          bg: "bg-life-light",
          border: "border-life/20",
          icon: <Heart size={18} className="text-life" />,
          label: "Life",
        };
      default:
        return {
          bg: "bg-other-light",
          border: "border-other/20",
          icon: <HelpCircle size={18} className="text-other" />,
          label: "Other",
        };
    }
  };

  const style = getCategoryStyle();

  // Format date
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("ja-JP", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Link
      to={`/chat/${topic.id}`}
      className={`
        block bg-white rounded-xl border p-4 
        hover:shadow-md hover:border-primary-200 
        transition-all duration-200 animate-fade-in
        ${className}
      `}
    >
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-xl ${style.bg}`}>
          <MessageCircle size={24} className="text-primary-600" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-primary-950 truncate">
              {topic.name}
            </h3>
            <span
              className={`
                inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs
                ${style.bg} ${style.border} border
              `}
            >
              {style.icon}
              {style.label}
            </span>
          </div>

          <p className="text-sm text-slate-500 truncate">
            {topic.description || "No description"}
          </p>

          <p className="text-xs text-slate-400 mt-2">
            Created {formatDate(topic.createdAt)}
          </p>
        </div>
      </div>
    </Link>
  );
}

